import { type ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { RouteFallback } from '@/routes/RouteFallback'

import { NoAccessPage } from './NoAccessPage'
import { useAdminSession } from './session'

/**
 * WHERE THE LOOP IS PREVENTED.
 *
 * Exactly one of the two components below navigates in each direction, and
 * neither ever navigates on `denied`:
 *
 *            signed-out ──── RequireAdmin ────▶ /login
 *            authorized ──── RedirectIfSignedIn ────▶ the page they wanted
 *            denied     ──── (nobody navigates) ────▶ <NoAccessPage/>, in place
 *
 * A guard that only knew `!isAdmin` would have `/` bounce to `/login` and
 * `/login` bounce back to `/` for an authenticated non-admin, for ever. The
 * five-state machine in `session.ts` exists so that "not signed in" and "not
 * allowed" are answered by different code paths, and this file is where that
 * distinction earns its keep.
 *
 * ⚠️ THIS GUARD DECIDES WHAT RENDERS. IT DOES NOT SECURE ANY DATA. Everything it
 * does happens in a bundle the visitor downloaded and can edit, so it is a
 * usability boundary, not a security one. The security boundary is RLS, on every
 * table, evaluated in Postgres against the JWT. Docs/08: "Route guards AND RLS
 * both enforce this. Never one without the other."
 */
export function RequireAdmin(): ReactNode {
  const { status } = useAdminSession()
  const location = useLocation()

  // `loading` is a STATE, not the absence of one. Rendering the ordinary route
  // skeleton here is what stops the first paint from being a white screen while
  // `getSession()` and `is_admin()` resolve.
  if (status === 'loading') return <RouteFallback />

  if (status === 'signed-out') {
    // `replace` so the protected URL does not sit in history behind the login
    // form — Back would otherwise bounce straight off this guard again.
    // `state.from` is what sends them where they were going once they are in.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    )
  }

  // `denied` and `unconfigured`. Terminal, rendered where they stand.
  if (status !== 'authorized') return <NoAccessPage />

  return <Outlet />
}

/** Where `RequireAdmin` sent them, when the state survived the navigation. */
function intendedPath(state: unknown): string {
  if (typeof state !== 'object' || state === null) return '/'
  const from = (state as { from?: unknown }).from
  // Only a same-site absolute path. A `from` of `https://elsewhere.example` or
  // `//elsewhere.example` would turn the login form into an open redirect.
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) return '/'
  if (from.startsWith('/login')) return '/'
  return from
}

/**
 * The `/login` side of the same decision.
 *
 * An operator who is already in should never be shown a password form — but a
 * `denied` one must NOT be redirected away either, or they would be sent to a
 * route that immediately sends them back. They get the same terminal screen the
 * guard shows, with the sign-out button on it.
 */
export function RedirectIfSignedIn({ children }: { children: ReactNode }): ReactNode {
  const { status } = useAdminSession()
  const location = useLocation()

  if (status === 'loading') return <RouteFallback />
  if (status === 'authorized') return <Navigate to={intendedPath(location.state)} replace />
  if (status === 'denied') return <NoAccessPage />

  // `signed-out` and `unconfigured` both render the form: an unconfigured build
  // has to say so on the screen where someone is trying to sign in, and the form
  // itself carries that banner.
  return children
}
